import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Package, Car, MapPin, Calendar, ShieldCheck, Star, Euro, Search, 
  ArrowRight, CheckCircle, Clock, Users, Zap, Heart, Phone, Mail,
  Facebook, Instagram, Linkedin, Twitter, ChevronRight, Quote, Shield,
  CreditCard, TrendingUp, Truck, Globe
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const LandingPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('send'); // 'send' or 'transport'
  const [searchData, setSearchData] = useState({
    departure: '',
    arrival: '',
    date: ''
  });

  const handleSearch = (e) => {
    e.preventDefault();
    if (activeTab === 'send') {
      navigate('/register?role=sender');
    } else {
      navigate('/register?role=carrier');
    }
  };

  const stats = [
    { value: '50K+', label: 'Colis livrés', icon: Package },
    { value: '15K+', label: 'Utilisateurs actifs', icon: Users },
    { value: '4.8/5', label: 'Note moyenne', icon: Star },
    { value: '-60%', label: 'vs transporteurs', icon: TrendingUp },
  ];

  const steps = [
    { 
      step: '01', 
      title: 'Publiez votre annonce', 
      description: 'Décrivez votre colis ou votre trajet en quelques clics',
      icon: Search,
      color: 'bg-orange-500'
    },
    { 
      step: '02', 
      title: 'Trouvez votre match', 
      description: 'Notre algorithme trouve le cotransporteur idéal',
      icon: Users,
      color: 'bg-blue-500'
    },
    { 
      step: '03', 
      title: 'Échangez en confiance', 
      description: 'Paiement sécurisé et identités vérifiées',
      icon: CheckCircle,
      color: 'bg-green-500'
    },
  ];

  const senderBenefits = [
    { icon: Euro, title: 'Économisez jusqu\'à 60%', desc: 'Par rapport aux transporteurs traditionnels' },
    { icon: Clock, title: 'Livraison rapide', desc: 'Souvent plus vite que les services classiques' },
    { icon: ShieldCheck, title: 'Paiement sécurisé', desc: 'Votre argent est protégé par Stripe' },
    { icon: Heart, title: 'Écologique', desc: 'Réutilisez des trajets existants' },
  ];

  const carrierBenefits = [
    { icon: Euro, title: 'Rentabilisez vos trajets', desc: 'Gagnez de l\'argent en transportant des colis' },
    { icon: Zap, title: 'Flexibilité totale', desc: 'Choisissez quand et quoi transporter' },
    { icon: Shield, title: 'Identité vérifiée', desc: 'Travaillez avec des expéditeurs de confiance' },
    { icon: TrendingUp, title: 'Revenus récurrents', desc: 'Jusqu\'à 500€/mois en trajets réguliers' },
  ];

  const testimonials = [
    {
      name: 'Marie L.',
      role: 'Expéditrice',
      avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop',
      rating: 5,
      text: 'J\'ai envoyé un meuble de Paris à Lyon pour 35€ au lieu de 150€ chez un transporteur. Incroyable!'
    },
    {
      name: 'Thomas D.',
      role: 'Cotransporteur',
      avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop',
      rating: 5,
      text: 'Je fais Paris-Bordeaux chaque semaine. Avec COLISFLASH, je gagne 200€/mois en transportant des colis.'
    },
    {
      name: 'Sophie M.',
      role: 'Expéditrice',
      avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop',
      rating: 5,
      text: 'Service au top! Mon colis est arrivé le lendemain et le cotransporteur était très sympa.'
    },
  ];

  const trustFeatures = [
    { icon: ShieldCheck, title: 'Identités vérifiées', desc: 'Vérification Stripe Identity' },
    { icon: CreditCard, title: 'Paiement sécurisé', desc: 'Protection acheteur incluse' },
    { icon: Star, title: 'Avis certifiés', desc: 'Système de notation mutuelle' },
    { icon: Phone, title: 'Support réactif', desc: 'Assistance 7j/7' },
  ];

  const faqs = [
    { q: 'Comment fonctionne le paiement?', a: 'Vous payez en ligne via Stripe. L\'argent est transféré au cotransporteur après confirmation de livraison.' },
    { q: 'Mes colis sont-ils assurés?', a: 'Les cotransporteurs vérifiés bénéficient d\'une assurance de base. Des options d\'assurance complémentaire sont disponibles.' },
    { q: 'Comment sont vérifiés les utilisateurs?', a: 'Nous utilisons Stripe Identity pour vérifier les pièces d\'identité (CNI, passeport, permis).' },
    { q: 'Combien puis-je gagner comme cotransporteur?', a: 'En moyenne, nos cotransporteurs gagnent entre 100€ et 500€/mois selon la fréquence de leurs trajets.' },
  ];

  return (
    <div className="min-h-screen bg-white font-['Manrope']">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-lg border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            <Link to="/" className="flex items-center gap-2 group">
              <div className="w-10 h-10 bg-orange-500 rounded-xl flex items-center justify-center transform group-hover:rotate-6 transition-transform">
                <Package className="w-6 h-6 text-white" />
              </div>
              <span className="font-['Barlow_Condensed'] text-2xl font-bold text-slate-900">COLISFLASH</span>
            </Link>
            
            <div className="hidden md:flex items-center gap-8">
              <a href="#how-it-works" className="text-slate-600 hover:text-slate-900 font-medium transition-colors">Comment ça marche</a>
              <a href="#benefits" className="text-slate-600 hover:text-slate-900 font-medium transition-colors">Avantages</a>
              <a href="#testimonials" className="text-slate-600 hover:text-slate-900 font-medium transition-colors">Témoignages</a>
              <a href="#faq" className="text-slate-600 hover:text-slate-900 font-medium transition-colors">FAQ</a>
            </div>
            
            <div className="flex items-center gap-3">
              <Link to="/login">
                <Button variant="ghost" className="font-semibold text-sm sm:text-base">
                  Connexion
                </Button>
              </Link>
              <Link to="/register">
                <Button className="bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-full px-4 sm:px-6 shadow-lg hover:shadow-orange-500/30 transform hover:-translate-y-0.5 transition-all text-sm sm:text-base">
                  S&apos;inscrire
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-24 md:pt-32 pb-16 md:pb-24 overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 bg-gradient-to-br from-orange-50 via-white to-slate-50" />
        <div className="absolute top-20 right-0 w-96 h-96 bg-orange-200 rounded-full blur-3xl opacity-30" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-200 rounded-full blur-3xl opacity-20" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Left: Content */}
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2 bg-orange-100 text-orange-700 px-4 py-2 rounded-full text-sm font-semibold">
                <Zap className="w-4 h-4" />
                Nouveau: Vérification d'identité Stripe
              </div>
              
              <h1 className="font-['Barlow_Condensed'] text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 leading-tight">
                Envoyez vos colis
                <span className="text-orange-500 block">jusqu'à 60% moins cher</span>
              </h1>
              
              <p className="text-lg sm:text-xl text-slate-600 max-w-xl">
                COLISFLASH connecte les expéditeurs avec des voyageurs qui font déjà le trajet. 
                <strong className="text-slate-900"> Économique, écologique et rapide.</strong>
              </p>

              {/* Search Widget */}
              <div className="bg-white rounded-2xl shadow-xl border border-slate-100 p-2 max-w-xl">
                {/* Tabs */}
                <div className="flex mb-4">
                  <button
                    onClick={() => setActiveTab('send')}
                    className={`flex-1 py-3 px-4 rounded-xl font-semibold transition-all ${
                      activeTab === 'send' 
                        ? 'bg-orange-500 text-white shadow-lg' 
                        : 'text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    <Package className="w-5 h-5 inline mr-2" />
                    J'envoie un colis
                  </button>
                  <button
                    onClick={() => setActiveTab('transport')}
                    className={`flex-1 py-3 px-4 rounded-xl font-semibold transition-all ${
                      activeTab === 'transport' 
                        ? 'bg-orange-500 text-white shadow-lg' 
                        : 'text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    <Car className="w-5 h-5 inline mr-2" />
                    Je transporte
                  </button>
                </div>
                
                {/* Search Form */}
                <form onSubmit={handleSearch} className="space-y-3 p-2">
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div className="relative">
                      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-green-500" />
                      <Input
                        placeholder="Ville de départ"
                        value={searchData.departure}
                        onChange={(e) => setSearchData({...searchData, departure: e.target.value})}
                        className="pl-10 h-12 bg-slate-50 border-slate-200 rounded-xl"
                      />
                    </div>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-red-500" />
                      <Input
                        placeholder="Ville d'arrivée"
                        value={searchData.arrival}
                        onChange={(e) => setSearchData({...searchData, arrival: e.target.value})}
                        className="pl-10 h-12 bg-slate-50 border-slate-200 rounded-xl"
                      />
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <div className="relative flex-1">
                      <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <Input
                        type="date"
                        value={searchData.date}
                        onChange={(e) => setSearchData({...searchData, date: e.target.value})}
                        className="pl-10 h-12 bg-slate-50 border-slate-200 rounded-xl"
                      />
                    </div>
                    <Button 
                      type="submit"
                      className="bg-orange-500 hover:bg-orange-600 text-white font-bold h-12 px-8 rounded-xl shadow-lg hover:shadow-orange-500/30 transform hover:-translate-y-0.5 transition-all"
                    >
                      <Search className="w-5 h-5 mr-2" />
                      Rechercher
                    </Button>
                  </div>
                </form>
              </div>

              {/* Trust badges */}
              <div className="flex flex-wrap items-center gap-6 text-sm text-slate-500">
                <div className="flex items-center gap-2">
                  <ShieldCheck className="w-5 h-5 text-green-500" />
                  <span>Paiement sécurisé</span>
                </div>
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-blue-500" />
                  <span>15 000+ utilisateurs</span>
                </div>
                <div className="flex items-center gap-2">
                  <Star className="w-5 h-5 text-yellow-500" />
                  <span>4.8/5 étoiles</span>
                </div>
              </div>
            </div>

            {/* Right: Image */}
            <div className="relative hidden lg:block">
              <div className="relative">
                <img
                  src="https://images.unsplash.com/photo-1553076489-80b7a25a948b?w=600&h=700&fit=crop"
                  alt="Chargement de colis dans une voiture"
                  className="rounded-3xl shadow-2xl object-cover w-full h-[500px]"
                />
                {/* Floating card */}
                <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl p-4 border border-slate-100">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                      <CheckCircle className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <p className="font-bold text-slate-900">Économie réalisée</p>
                      <p className="text-green-600 font-semibold">-115€ sur ce colis</p>
                    </div>
                  </div>
                </div>
                {/* Another floating element */}
                <div className="absolute -top-4 -right-4 bg-orange-500 text-white rounded-2xl shadow-xl p-4">
                  <p className="font-['Barlow_Condensed'] text-3xl font-bold">-60%</p>
                  <p className="text-sm opacity-90">vs transporteurs</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, idx) => (
              <div key={idx} className="text-center">
                <stat.icon className="w-8 h-8 text-orange-500 mx-auto mb-3" />
                <p className="font-['Barlow_Condensed'] text-3xl sm:text-4xl font-bold">{stat.value}</p>
                <p className="text-slate-400 text-sm mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-20 md:py-28 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block bg-orange-100 text-orange-700 px-4 py-1 rounded-full text-sm font-semibold mb-4">
              SIMPLE & RAPIDE
            </span>
            <h2 className="font-['Barlow_Condensed'] text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 uppercase">
              Comment ça marche?
            </h2>
            <p className="text-slate-600 mt-4 max-w-2xl mx-auto text-lg">
              En 3 étapes simples, envoyez ou transportez des colis partout en France
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 relative">
            {/* Connection line */}
            <div className="hidden md:block absolute top-24 left-1/4 right-1/4 h-0.5 border-t-2 border-dashed border-slate-300" />
            
            {steps.map((step, idx) => (
              <div key={idx} className="relative text-center group">
                <div className={`w-20 h-20 ${step.color} rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg transform group-hover:scale-110 transition-transform`}>
                  <step.icon className="w-10 h-10 text-white" />
                </div>
                <span className="font-['Barlow_Condensed'] text-6xl font-bold text-slate-100 absolute -top-4 left-1/2 -translate-x-1/2 -z-10">
                  {step.step}
                </span>
                <h3 className="font-['Barlow_Condensed'] text-xl font-bold text-slate-900 uppercase mb-2">
                  {step.title}
                </h3>
                <p className="text-slate-600">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section - Bento Grid */}
      <section id="benefits" className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block bg-orange-100 text-orange-700 px-4 py-1 rounded-full text-sm font-semibold mb-4">
              POUR TOUS
            </span>
            <h2 className="font-['Barlow_Condensed'] text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 uppercase">
              Tout le monde y gagne
            </h2>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Senders */}
            <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-3xl p-8 border border-orange-100">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-orange-500 rounded-xl flex items-center justify-center">
                  <Package className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-['Barlow_Condensed'] text-2xl font-bold text-slate-900 uppercase">
                  Pour les Expéditeurs
                </h3>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                {senderBenefits.map((benefit, idx) => (
                  <div key={idx} className="bg-white/80 rounded-xl p-4 border border-orange-100 hover:shadow-md transition-shadow">
                    <benefit.icon className="w-8 h-8 text-orange-500 mb-3" />
                    <h4 className="font-semibold text-slate-900 mb-1">{benefit.title}</h4>
                    <p className="text-sm text-slate-600">{benefit.desc}</p>
                  </div>
                ))}
              </div>
              <Link to="/register?role=sender">
                <Button className="w-full mt-6 bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-xl h-12">
                  Envoyer un colis
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>

            {/* Carriers */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-3xl p-8 border border-blue-100">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center">
                  <Car className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-['Barlow_Condensed'] text-2xl font-bold text-slate-900 uppercase">
                  Pour les Voyageurs
                </h3>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                {carrierBenefits.map((benefit, idx) => (
                  <div key={idx} className="bg-white/80 rounded-xl p-4 border border-blue-100 hover:shadow-md transition-shadow">
                    <benefit.icon className="w-8 h-8 text-blue-500 mb-3" />
                    <h4 className="font-semibold text-slate-900 mb-1">{benefit.title}</h4>
                    <p className="text-sm text-slate-600">{benefit.desc}</p>
                  </div>
                ))}
              </div>
              <Link to="/register?role=carrier">
                <Button className="w-full mt-6 bg-blue-500 hover:bg-blue-600 text-white font-bold rounded-xl h-12">
                  Devenir cotransporteur
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-16 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-['Barlow_Condensed'] text-2xl sm:text-3xl font-bold uppercase">
              Votre confiance, notre priorité
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {trustFeatures.map((feature, idx) => (
              <div key={idx} className="text-center p-6 bg-slate-800/50 rounded-2xl hover:bg-slate-800 transition-colors">
                <feature.icon className="w-10 h-10 text-orange-500 mx-auto mb-4" />
                <h4 className="font-semibold mb-1">{feature.title}</h4>
                <p className="text-sm text-slate-400">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonials" className="py-20 md:py-28 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block bg-orange-100 text-orange-700 px-4 py-1 rounded-full text-sm font-semibold mb-4">
              TÉMOIGNAGES
            </span>
            <h2 className="font-['Barlow_Condensed'] text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 uppercase">
              Ils nous font confiance
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, idx) => (
              <div key={idx} className="bg-white rounded-2xl p-6 shadow-lg border border-slate-100 hover:shadow-xl transition-shadow">
                <Quote className="w-10 h-10 text-orange-200 mb-4" />
                <p className="text-slate-600 mb-6 italic">"{testimonial.text}"</p>
                <div className="flex items-center gap-4">
                  <img 
                    src={testimonial.avatar} 
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                  <div>
                    <p className="font-semibold text-slate-900">{testimonial.name}</p>
                    <p className="text-sm text-slate-500">{testimonial.role}</p>
                  </div>
                  <div className="ml-auto flex">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 text-yellow-500 fill-current" />
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-20 md:py-28">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block bg-orange-100 text-orange-700 px-4 py-1 rounded-full text-sm font-semibold mb-4">
              FAQ
            </span>
            <h2 className="font-['Barlow_Condensed'] text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 uppercase">
              Questions fréquentes
            </h2>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, idx) => (
              <details key={idx} className="group bg-white rounded-xl border border-slate-200 overflow-hidden">
                <summary className="flex items-center justify-between p-6 cursor-pointer font-semibold text-slate-900 hover:bg-slate-50">
                  {faq.q}
                  <ChevronRight className="w-5 h-5 text-slate-400 group-open:rotate-90 transition-transform" />
                </summary>
                <div className="px-6 pb-6 text-slate-600">
                  {faq.a}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-28 bg-gradient-to-br from-orange-500 to-orange-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-['Barlow_Condensed'] text-3xl sm:text-4xl lg:text-5xl font-bold uppercase mb-6">
            Prêt à économiser sur vos envois?
          </h2>
          <p className="text-xl text-orange-100 mb-8 max-w-2xl mx-auto">
            Rejoignez plus de 15 000 utilisateurs qui font confiance à COLISFLASH pour leurs envois de colis.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register?role=sender">
              <Button className="bg-white text-orange-600 hover:bg-orange-50 font-bold rounded-full px-8 py-4 text-lg shadow-xl">
                <Package className="w-5 h-5 mr-2" />
                Envoyer un colis
              </Button>
            </Link>
            <Link to="/register?role=carrier">
              <Button className="bg-orange-700 hover:bg-orange-800 text-white font-bold rounded-full px-8 py-4 text-lg border-2 border-orange-400">
                <Car className="w-5 h-5 mr-2" />
                Devenir cotransporteur
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12">
            <div className="col-span-2 md:col-span-1">
              <Link to="/" className="flex items-center gap-2 mb-4">
                <div className="w-10 h-10 bg-orange-500 rounded-xl flex items-center justify-center">
                  <Package className="w-6 h-6 text-white" />
                </div>
                <span className="font-['Barlow_Condensed'] text-xl font-bold">COLISFLASH</span>
              </Link>
              <p className="text-slate-400 text-sm">
                La plateforme de covoiturage de colis #1 en France.
              </p>
              <div className="flex gap-4 mt-4">
                <a href="#" className="text-slate-400 hover:text-white transition-colors">
                  <Facebook className="w-5 h-5" />
                </a>
                <a href="#" className="text-slate-400 hover:text-white transition-colors">
                  <Instagram className="w-5 h-5" />
                </a>
                <a href="#" className="text-slate-400 hover:text-white transition-colors">
                  <Linkedin className="w-5 h-5" />
                </a>
                <a href="#" className="text-slate-400 hover:text-white transition-colors">
                  <Twitter className="w-5 h-5" />
                </a>
              </div>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Services</h4>
              <ul className="space-y-2 text-slate-400 text-sm">
                <li><a href="#" className="hover:text-white transition-colors">Envoyer un colis</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Devenir cotransporteur</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Tarifs</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Assurance</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Entreprise</h4>
              <ul className="space-y-2 text-slate-400 text-sm">
                <li><a href="#" className="hover:text-white transition-colors">À propos</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Blog</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Carrières</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Presse</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Support</h4>
              <ul className="space-y-2 text-slate-400 text-sm">
                <li><a href="#" className="hover:text-white transition-colors">Centre d'aide</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Contact</a></li>
                <li><Link to="/cgu" className="hover:text-white transition-colors">CGU</Link></li>
                <li><Link to="/privacy" className="hover:text-white transition-colors">Confidentialité</Link></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-slate-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-slate-400 text-sm">
              © 2025 COLISFLASH. Tous droits réservés.
            </p>
            <div className="flex items-center gap-4">
              <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/b/ba/Stripe_Logo%2C_revised_2016.svg/120px-Stripe_Logo%2C_revised_2016.svg.png" alt="Stripe" className="h-6 opacity-50" />
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
